import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

const initialState = {
  certificates: [] as Certificate[],
  isCertificatesRequested: false as boolean,
  companies: [] as Company[],
  isCompaniesRequested: false as boolean,
  certificateInstances: [] as CertificateInstance[],
  isCertificateInstancesRequested: false as boolean,
  users: [] as User[],
  isUsersRequested: false as boolean,
  certificateTypes: [] as CertificateType[],
  isCertificateTypesRequested: false as boolean,
  loggings: [] as Logging[],
  isLoggingsRequested: false as boolean,
  enrichedCertificates: [] as EnrichedCertificate[],
}
const dataSlice = createSlice({
  name: 'interface',
  initialState,
  reducers: {
    setCertificates(state, action: PayloadAction<Certificate[]>) {
      state.certificates = action.payload
    },
    setIsCertificatesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificatesRequested = action.payload
    },
    setCompanies(state, action: PayloadAction<Company[]>) {
      state.companies = action.payload
    },
    setIsCompaniesRequested(state, action: PayloadAction<boolean>) {
      state.isCompaniesRequested = action.payload
    },
    setCertificateInstances(
      state,
      action: PayloadAction<CertificateInstance[]>
    ) {
      state.certificateInstances = action.payload
    },
    setIsCertificateInstancesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificateInstancesRequested = action.payload
    },
    setUsers(state, action: PayloadAction<User[]>) {
      state.users = action.payload
    },
    setIsUsersRequested(state, action: PayloadAction<boolean>) {
      state.isUsersRequested = action.payload
    },
    setCertificateTypes(state, action: PayloadAction<CertificateType[]>) {
      state.certificateTypes = action.payload
    },
    setIsCertificateTypesRequested(state, action: PayloadAction<boolean>) {
      state.isCertificateTypesRequested = action.payload
    },
    setLoggings(state, action: PayloadAction<Logging[]>) {
      state.loggings = action.payload
    },
    setIsLoggingsRequested(state, action: PayloadAction<boolean>) {
      state.isLoggingsRequested = action.payload
    },
    setEnrichedCertificates(
      state,
      action: PayloadAction<EnrichedCertificate[]>
    ) {
      state.enrichedCertificates = action.payload
    },
  },
})

export const {
  setCertificates,
  setIsCertificatesRequested,
  setCompanies,
  setIsCompaniesRequested,
  setCertificateInstances,
  setIsCertificateInstancesRequested,
  setUsers,
  setIsUsersRequested,
  setCertificateTypes,
  setIsCertificateTypesRequested,
  setLoggings,
  setIsLoggingsRequested,
  setEnrichedCertificates,
} = dataSlice.actions

export default dataSlice.reducer

// 'Expired' is an extra calculated state based on status valid|prolonged + validFrom,ValidUntil&ExtendedUntil
export const certificateStatuses = [
  'Valid',
  'Prolonged',
  'Suspended',
  'Withdrawn',
] as const
export type CertificateStatus = (typeof certificateStatuses)[number]
export type CertificateStatusesExpired = 'Expired'
export const certificateStatusesExpired: CertificateStatusesExpired = 'Expired'

export const CertificateScopeMaxLength = 5000
export interface Certificate {
  uuid: string // primary key
  certificateNumber: string
  scope: string
  naceCodes: string[]
  companyBranches: CertificateCompanyBranch[]
  status: CertificateStatus
  certificateInstanceVatNumber: string
  type: string // CertificateType.name
  validFrom: number
  validUntil: number
  extendedUntil: number | undefined
  issueDate: number
  isGroupCertificate: boolean
}
export interface CertificateCompanyBranch {
  vatNumber: string
  branchNumbers: string[]
}

export interface Company {
  vatNumber: string // primary key
  name: string
  legalForm: string
  email: string
  website: string
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
  naceCode: string
  branches: CompanyBranch[]
  accidentCounts: CompanyAccidentCount[]
  lastKpbCheck: number | undefined
  lastKpbUpdate: number | undefined
  lastKpbCheckSuccess: boolean | undefined
}

export interface CompanyAccidentCount {
  year: number
  count: number
}

export interface CompanyBranch {
  number: string
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
}

export interface CertificateInstance {
  vatNumber: string // primary key
  name: string
  email: string
  website: string
  telephone: string
  addressLine1: string
  postalCode: string
  city: string
  countryCode: string
  coordinators: CertificateInstanceCoordinator[]
}

export interface CertificateInstanceCoordinator {
  uuid: string
  name: string
  email: string
  telephone: string
}

export interface User {
  email: string // primary key, stored in lowercase
  isAdmin: boolean // if true, is admin, can manage all certificateInstances and some extra
  certificateInstanceVatNumber?: string // if not-empty, can manage that certificateInstance
}

export interface CertificateType {
  name: string
  isActive: boolean
  naceLimitation: string[]
}

export interface Logging {
  uuid: string
  userEmail: string
  changeDate: number
  action: string
  details: string
}

export interface EnrichedCertificate extends Certificate {
  calculatedStatus: CertificateStatus | CertificateStatusesExpired | ''
  companyNames: string
  companyPostcodes: string
  certificateInstanceName: string
}

export type queryKboResponse = {
  success: true
  lookupData: {
    vatNumber: string
    name: string
    legalForm: string
    addressLine1: string
    postalCode: string
    city: string
    countryCode: string
    naceCode: string
    branches: CompanyBranch[]
  }
} | {
  success: false
  error?: string
}